<template>
  <article :id="phase.phaseId" class="flex w-full scroll-mt-10 flex-col gap-3">
    <BaseCard type="filled" class="flex flex-col p-5">
      <div class="mb-8 flex flex-col gap-1">
        <div class="flex flex-row items-center gap-3">
          <div class="relative h-10 w-10 shrink-0">
            <span
              class="text-on-primary text-headline-small absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
            >
              {{ phase.number }}
            </span>
            <BaseShape class="text-primary h-full w-full" shape="9-sided-cookie" />
          </div>
          <h3 class="text-title-medium grow">
            {{ phase.title }}
          </h3>
          <span class="text-body-medium text-on-surface-variant">
            {{ startTime + '-' + (startTime + phase.durationMinutes) + '′' }}</span
          >
          <BaseChip
            >{{ $t('lumia.durationMinutes', { minutes: phase.durationMinutes }) }}
          </BaseChip>
        </div>
        <div class="flex flex-row flex-wrap gap-1 pl-12">
          <BaseChip>{{ $t(`lumia.phase.types.${phase.type}`) }}</BaseChip>
          <BaseChip v-if="phase.socialForm">{{ phase.socialForm.label }}</BaseChip>
          <BaseChip v-if="phase.level">{{ phase.level.label }}</BaseChip>
        </div>
      </div>
      <div class="flex flex-col">
        <div
          v-if="phase.type != 'kompetenzziel'"
          class="bg-secondary-container text-on-secondary-container rounded-medium p-3"
        >
          <div class="mb-1 flex flex-row items-center gap-1">
            <BaseIcon size="20">target</BaseIcon>
            <h4 class="text-title-small">{{ $t('lumia.phase.goal') }}</h4>
          </div>
          <LessonImpulseField class="text-body-large" :value="phase.goal" />
        </div>
        <LessonImpulseField
          v-if="phase.type != 'reflexion' && phase.type != 'kompetenzziel'"
          :value="phase.description"
          class="text-body-large mt-6 px-3"
        />
        <ReflexionPhase
          v-else-if="phase.type == 'reflexion'"
          :reflexion-questions="reflexionQuestions"
          :indicators="indicators"
          :lesson-feedback="lessonFeedback"
        />
        <GoalPhase
          v-else-if="phase.type == 'kompetenzziel'"
          :mission="mission"
          :indicators="indicators"
        />
        <div v-if="phase.observationTip || materials.length" class="mt-6 flex flex-col gap-2">
          <div
            v-if="phase.observationTip"
            class="bg-surface-container-highest text-on-surface-variant rounded-medium group relative cursor-pointer overflow-hidden p-3"
            @click="showTipps = !showTipps"
          >
            <div class="flex flex-row items-center gap-2">
              <BaseIcon class="text-on-surface-variant">lightbulb</BaseIcon>
              <h4 class="text-title-small grow">{{ $t('lumia.phase.observation') }}</h4>
              <span
                class="text-label-medium text-on-surface-variant bg-surface-container-low inline-flex h-6 w-6 items-center justify-center rounded-full"
                >{{ numberOfTips }}</span
              >
              <BaseIcon class="text-on-surface-variant">{{
                showTipps ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
              }}</BaseIcon>
            </div>
            <BaseAnimatedExtend :is-extended="showTipps">
              <LessonImpulseField html :value="phase.observationTip" />
            </BaseAnimatedExtend>
            <BaseStateLayer />
          </div>
          <div
            v-if="materials.length"
            class="bg-surface-container-highest text-on-surface-variant rounded-medium group relative cursor-pointer overflow-hidden p-3"
            @click="showMaterials = !showMaterials"
          >
            <div class="flex flex-row items-center gap-2">
              <BaseIcon class="text-on-surface-variant">folder</BaseIcon>
              <h4 class="text-title-small grow">{{ $t('lumia.sections.materials') }}</h4>
              <span
                class="text-label-medium text-on-surface-variant bg-surface-container-low inline-flex h-6 w-6 items-center justify-center rounded-full"
                >{{ materials.length }}</span
              >
              <BaseIcon class="text-on-surface-variant">{{
                showMaterials ? 'keyboard_arrow_up' : 'keyboard_arrow_down'
              }}</BaseIcon>
            </div>
            <BaseAnimatedExtend :is-extended="showMaterials">
              <div class="flex flex-col gap-2 pt-4">
                <LessonImpulseMaterialCard
                  v-for="material in materials"
                  :key="material.materialId"
                  :material="material"
                />
              </div>
            </BaseAnimatedExtend>
            <BaseStateLayer />
          </div>
        </div>
      </div>
    </BaseCard>
  </article>
</template>
<script lang="ts" setup>
  import BaseCard from '@/core/components/base/card/BaseCard.vue'
  import BaseChip from '@/core/components/base/chip/BaseChip.vue'
  import type { Member } from '@/core/models/member/member'
  import type { LessonTeam } from '../models/lesson'
  import type {
    Indikator,
    LessonImpulseMaterial,
    LessonImpulsePhase,
  } from '../models/lesson-impulse'
  import LessonImpulseField from './LessonImpulseField.vue'
  import ReflexionPhase from './ReflexionPhase.vue'
  import LessonImpulseMaterialCard from './LessonImpulseMaterialCard.vue'
  import BaseShape from '@/core/components/base/shapes/BaseShape.vue'
  import { computed, ref } from 'vue'
  import BaseAnimatedExtend from '@/core/components/base/animated-extend/BaseAnimatedExtend.vue'
  import BaseIcon from '@/core/components/base/icon/BaseIcon.vue'
  import BaseStateLayer from '@/core/components/base/state-layer/BaseStateLayer.vue'
  import GoalPhase from './GoalPhase.vue'

  interface Props {
    phase: LessonImpulsePhase
    materials: ReadonlyArray<LessonImpulseMaterial>
    startTime: number
    reflexionQuestions: ReadonlyArray<{ id: string; text: string }>
    indicators: ReadonlyArray<Indikator>
    mission: string | null
    lessonFeedback?: {
      readonly running: boolean
      readonly teams: ReadonlyArray<LessonTeam>
      readonly members: ReadonlyArray<Member>
    }
  }

  const props = defineProps<Props>()

  const showTipps = ref(false)
  const showMaterials = ref(false)

  const numberOfTips = computed(() => {
    if (!props.phase.observationTip) {
      return 0
    }
    return props.phase.observationTip.split('<li>').length - 1
  })
</script>
